import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function RevenueFlow({ practiceId }) {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [channels, setChannels] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const [dashResult, channelResult, trendResult] = await Promise.all([
        api.get(`/practices/${practiceId}/revenueflow/dashboard`),
        api.get(`/practices/${practiceId}/revenueflow/channels`),
        api.get(`/practices/${practiceId}/revenueflow/trends?days=30`),
      ]);
      if (dashResult.success) setDashboard(dashResult.data);
      if (channelResult.success) setChannels(channelResult.data || []);
      if (trendResult.success) setTrend(trendResult.data || []);
    } catch (err) {
      console.error('Error loading RevenueFlow data:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><p>Loading RevenueFlow dashboard...</p></div>;
  }

  const kpis = dashboard?.kpis || {};
  const aging = dashboard?.aging || {};
  const funnel = dashboard?.funnel || {};

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>RevenueFlow</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/practice-view/billing-escalations')}
            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}>
            Escalations
          </button>
          <button onClick={() => navigate('/practice-view/payment-settings')}
            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: '0.875rem' }}>
            Settings
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <KPICard label="Total A/R" value={`$${(kpis.totalAR || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="#DC2626" />
        <KPICard label="Total Collected" value={`$${(kpis.totalCollected || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="#059669" />
        <KPICard label="Avg Days Outstanding" value={kpis.avgDaysOutstanding || 0} color="#CA8A04" />
        <KPICard label="Active Campaigns" value={kpis.activeCampaigns || 0} color="#2563EB" />
        <KPICard label="Conversion Rate" value={`${kpis.conversionRate || 0}%`} color="#7C3AED" />
        <KPICard label="Accounts" value={kpis.totalAccounts || 0} color="#6B7280" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* AR Aging */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>A/R Aging</h3>
          <AgingBar label="Current" amount={aging.current_amount || 0} count={aging.current_count || 0} color="#059669" />
          <AgingBar label="30 Days" amount={aging.overdue_30 || 0} count={aging.overdue_30_count || 0} color="#CA8A04" />
          <AgingBar label="60 Days" amount={aging.overdue_60 || 0} count={aging.overdue_60_count || 0} color="#EA580C" />
          <AgingBar label="90+ Days" amount={aging.overdue_90 || 0} count={aging.overdue_90_count || 0} color="#DC2626" />
          <AgingBar label="Collections" amount={aging.collections || 0} count={aging.collections_count || 0} color="#7F1D1D" />
        </div>

        {/* Conversion Funnel */}
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>Conversion Funnel</h3>
          <FunnelStep label="Campaigns Created" value={funnel.campaigns || 0} />
          <FunnelStep label="Reminders Sent" value={funnel.remindersSent || 0} />
          <FunnelStep label="Link Clicks" value={funnel.linkClicks || 0} />
          <FunnelStep label="Payments Made" value={funnel.payments || 0} highlight />
        </div>
      </div>

      {/* Channel Effectiveness */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>Channel Effectiveness</h3>
        {channels.length === 0 ? (
          <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>No channel data yet</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, color: '#6B7280' }}>Channel</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: '#6B7280' }}>Sent</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: '#6B7280' }}>Delivered</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: '#6B7280' }}>Clicked</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: '#6B7280' }}>Converted</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: '#6B7280' }}>Collected</th>
              </tr>
            </thead>
            <tbody>
              {channels.map(ch => (
                <tr key={ch.channel} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '0.5rem', fontWeight: 500 }}>{ch.channel}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{ch.total_sent}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{ch.delivered}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{ch.clicked}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{ch.converted}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', color: '#059669', fontWeight: 600 }}>
                    ${(ch.amount_collected || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Collection Trend */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '1rem', color: '#374151' }}>Collections (Last 30 Days)</h3>
        {trend.length === 0 ? (
          <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>No collection data yet</p>
        ) : (
          <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '120px' }}>
            {trend.map(day => {
              const maxTotal = Math.max(...trend.map(t => t.total || 0), 1);
              const height = Math.max(4, ((day.total || 0) / maxTotal) * 100);
              return (
                <div key={day.day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: '100%', height: `${height}px`, background: '#059669', borderRadius: '2px 2px 0 0', minWidth: '4px' }}
                    title={`${day.day}: $${(day.total || 0).toFixed(2)} (${day.count} payments)`} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1rem' }}>
      <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function AgingBar({ label, amount, count, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
      <div style={{ width: '70px', fontSize: '0.75rem', color: '#6B7280' }}>{label}</div>
      <div style={{ flex: 1, height: '20px', background: '#F3F4F6', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: amount > 0 ? '100%' : '0%', background: color, borderRadius: '4px', transition: 'width 0.3s' }} />
      </div>
      <div style={{ width: '100px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600 }}>
        ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
      </div>
      <div style={{ width: '40px', textAlign: 'right', fontSize: '0.75rem', color: '#6B7280' }}>({count})</div>
    </div>
  );
}

function FunnelStep({ label, value, highlight }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.5rem 0.75rem', marginBottom: '0.25rem',
      background: highlight ? '#ECFDF5' : '#F9FAFB', borderRadius: '6px',
    }}>
      <span style={{ fontSize: '0.875rem', color: highlight ? '#059669' : '#374151' }}>{label}</span>
      <span style={{ fontSize: '1.125rem', fontWeight: 700, color: highlight ? '#059669' : '#111827' }}>{value}</span>
    </div>
  );
}

export default RevenueFlow;
